import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import wagonFillService from "services/admin/wagonFillService";

interface Props {
  from: string;
  to: string;
}

/**
 * Заполненность по рейсам/поездам (Фаза 2.0a). fill% считается только для рейсов
 * с заданным составом вагонов (вместимость известна); остальные показаны с пометкой.
 */
const FillAnalytics: React.FC<Props> = ({ from, to }) => {
  const orgID = localStorage.getItem("orgID") || "";

  const { data, isLoading } = useQuery({
    queryKey: ["wagon-fill", { orgID, from, to }],
    queryFn: async () => (await wagonFillService.getFill({ from, to })).data,
  });

  const num = (n: any) => Number(n || 0).toLocaleString("ru-RU");
  const pct = (n: number | null) => (n == null ? "—" : `${Math.round(n * 100)}%`);

  const summary = data?.summary;
  const byTrain: any[] = data?.byTrain || [];
  const runs: any[] = data?.runs || [];
  const hasData = runs.length > 0;

  const fillColor = (p: number | null) => {
    if (p == null) return "inherit";
    if (p > 1) return "#eb445a"; // перегруз
    if (p < (data?.thresholds?.underload ?? 0.6)) return "#d98a00"; // недогруз
    return "#2dd36f";
  };

  if (isLoading) {
    return (
      <div className="text-center m-t-32">
        <CircularProgress />
      </div>
    );
  }

  if (!hasData) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            За выбранный период нет рейсов. Рейсы создаются при оформлении отправок.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      {summary && summary.runsWithComposition < summary.runsCount && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Заполненность считается только для рейсов с заданным составом вагонов:{" "}
          <strong>
            {summary.runsWithComposition} из {summary.runsCount}
          </strong>
          . Задайте состав в разделе «Рейсы → Состав».
        </Alert>
      )}

      {/* Сводка */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        {[
          { label: "Рейсов", value: num(summary.runsCount) },
          { label: "С составом", value: `${num(summary.runsWithComposition)}` },
          {
            label: "Средняя заполненность",
            value: pct(summary.avgFillPct),
            accent: "#3880ff",
          },
          {
            label: "Недогруз (рейсов)",
            value: num(summary.underloadRuns),
            accent: summary.underloadRuns ? "#d98a00" : undefined,
          },
          {
            label: "Перегруз (рейсов)",
            value: num(summary.overloadRuns),
            accent: summary.overloadRuns ? "#eb445a" : undefined,
          },
          {
            label: "Загрузка / Вместимость, кг",
            value: `${num(summary.totalLoadedKg)} / ${num(summary.totalCapacityKg)}`,
          },
        ].map((c: any) => (
          <Card key={c.label} variant="outlined">
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {c.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: c.accent || "inherit" }}>
                {c.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* По поездам */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Заполненность по поездам (худшие сверху)
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Поезд</TableCell>
                  <TableCell align="right">Рейсов</TableCell>
                  <TableCell align="right">С составом</TableCell>
                  <TableCell align="right">Загрузка, кг</TableCell>
                  <TableCell align="right">Вместимость, кг</TableCell>
                  <TableCell align="right">Заполненность</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byTrain.map((t) => (
                  <TableRow key={t.trainID}>
                    <TableCell>{t.trainNumber || `#${t.trainID}`}</TableCell>
                    <TableCell align="right">{num(t.runs)}</TableCell>
                    <TableCell align="right">{num(t.runsWithComposition)}</TableCell>
                    <TableCell align="right">{num(t.loadedKg)}</TableCell>
                    <TableCell align="right">{num(t.capacityKg)}</TableCell>
                    <TableCell align="right">
                      <span style={{ color: fillColor(t.fillPct), fontWeight: 700 }}>
                        {pct(t.fillPct)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {/* По рейсам */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Рейсы
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Поезд</TableCell>
                  <TableCell>Маршрут</TableCell>
                  <TableCell align="right">Вагонов</TableCell>
                  <TableCell align="right">Загрузка, кг</TableCell>
                  <TableCell align="right">Вместимость, кг</TableCell>
                  <TableCell align="right">Заполненность</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.runID}>
                    <TableCell>{r.departureDate || "—"}</TableCell>
                    <TableCell>{r.trainNumber || `#${r.trainID}`}</TableCell>
                    <TableCell>
                      {r.originName} → {r.destinationName}
                    </TableCell>
                    <TableCell align="right">{num(r.wagonsCount)}</TableCell>
                    <TableCell align="right">{num(r.loadedKg)}</TableCell>
                    <TableCell align="right">{num(r.capacityKg)}</TableCell>
                    <TableCell align="right">
                      {!r.hasComposition ? (
                        <Chip size="small" label="нет состава" />
                      ) : (
                        <span style={{ color: fillColor(r.fillPct), fontWeight: 700 }}>
                          {pct(r.fillPct)}
                          {r.underload && " ↓"}
                          {r.overload && " ↑"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default FillAnalytics;
